import { useCallback, useEffect, useMemo, useState } from 'react';

export type StickyMode = 'full' | 'partial';

type SectionRequirements = {
  hasLeftAside: boolean;
  hasRightAside: boolean;
  hasBottomBanner: boolean;
};

export type PageContextType = {
  stickyMode: StickyMode;
  needsFixedHeight: boolean;
  registerSection: (requirements: SectionRequirements) => void;
  unregisterSection: () => void;
};

export const usePageContext = (stickyMode?: StickyMode): PageContextType => {
  const [sectionRequirements, setSectionRequirements] = useState<SectionRequirements | null>(null);

  const registerSection = useCallback((requirements: SectionRequirements) => {
    setSectionRequirements(requirements);
  }, []);

  const unregisterSection = useCallback(() => {
    setSectionRequirements(null);
  }, []);

  const hasFixedHeightProps = useMemo(() => {
    if (!sectionRequirements) {
      return false;
    }
    return sectionRequirements.hasLeftAside || sectionRequirements.hasRightAside || sectionRequirements.hasBottomBanner;
  }, [sectionRequirements]);

  const effectiveStickyMode = stickyMode ?? (hasFixedHeightProps ? 'full' : 'partial');

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && hasFixedHeightProps && !stickyMode) {
      console.warn(
        'Page component: Auto-setting stickyMode="full" because Page.Section uses leftAside, rightAside, or bottomBanner. ' +
          'Consider explicitly setting <Page stickyMode="full"> for clarity.',
      );
    }
  }, [hasFixedHeightProps, stickyMode]);

  const needsFixedHeight = hasFixedHeightProps;

  return useMemo(
    () => ({ stickyMode: effectiveStickyMode, needsFixedHeight, registerSection, unregisterSection }),
    [effectiveStickyMode, needsFixedHeight, registerSection, unregisterSection],
  );
};
